import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetAllFavouriteStores } from '@/modules/shop/hooks/useGetAllFavouriteStores';
import useUpdateFavouriteStore from '@/modules/shop/hooks/useUpdateFavouriteStore';
import {
  FavouriteStoresContext,
  FavouriteStoresContextType,
} from '@/modules/shop/providers/FavouriteStoresContext';

const FavouriteStoreProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data, mutate } = useGetAllFavouriteStores();
  const { updateFavouriteStore } = useUpdateFavouriteStore();
  const [favouriteStores, setFavouriteStores] = useState<
    Record<number, boolean>
  >({});
  useEffect(() => {
    const favStoreHash = (data?.data ?? []).reduce((acc, favStore) => {
      return { ...acc, [favStore.merchantId]: true };
    }, {});
    setFavouriteStores(favStoreHash);
  }, [data]);

  const updateStore = useCallback(
    async (merchantId: number, isFavourite: boolean) => {
      if (isFavourite) {
        setFavouriteStores({ ...favouriteStores, [merchantId]: true });
      } else {
        delete favouriteStores[merchantId];
        setFavouriteStores({ ...favouriteStores });
      }
      try {
        await updateFavouriteStore(merchantId, isFavourite);
      } catch (error) {
        setFavouriteStores({ ...favouriteStores });
        throw error;
      }

      // /api/member/favs does not return the updated list of favourite stores due to caching
      setTimeout(() => {
        mutate();
      }, 1000);
    },
    [favouriteStores, mutate, updateFavouriteStore]
  );

  const context: FavouriteStoresContextType | null = useMemo(() => {
    return {
      stores: favouriteStores,
      updateStore,
    } as FavouriteStoresContextType;
  }, [updateStore, favouriteStores]);

  return (
    <FavouriteStoresContext.Provider value={context}>
      {children}
    </FavouriteStoresContext.Provider>
  );
};

export default FavouriteStoreProvider;
